import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const r = await api.get("/auth/me");
      setUser(r.data);
      // SAFETY NET: every time we load /auth/me and the user is signed in but
      // NOT subscribed, fire-and-forget a silent /payment/recover call. If they
      // paid recently (PayPal webhook fired but their session cookie was
      // stripped during the redirect), this picks them up automatically — no
      // refresh, no extra clicks. The recover endpoint is idempotent and cheap.
      if (r.data && !r.data.is_subscribed) {
        api.post("/payment/recover").then((rr) => {
          if (rr.data?.recovered) {
            // We just granted them access — refetch /auth/me to update the UI.
            api.get("/auth/me").then((r2) => setUser(r2.data)).catch(() => {});
          }
        }).catch(() => {});
      }
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from Google OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
    // Pre-warm the PayPal SDK so /subscribe is instant when the user gets there.
    // Idle-callback fallback to setTimeout for older mobile browsers.
    const warm = () => {
      try {
        const cached = JSON.parse(localStorage.getItem("ybtv_paypal_cache") || "null");
        const cid = cached?.config?.paypal_client_id;
        if (cid && !document.getElementById("ybtv-paypal-preload")) {
          const l = document.createElement("link");
          l.id = "ybtv-paypal-preload";
          l.rel = "preload";
          l.as = "script";
          l.href = `https://www.paypal.com/sdk/js?client-id=${cid}&vault=true&intent=subscription&currency=USD`;
          l.crossOrigin = "anonymous";
          document.head.appendChild(l);
        }
      } catch (_) {}
    };
    if ("requestIdleCallback" in window) {
      window.requestIdleCallback(warm, { timeout: 2000 });
    } else {
      setTimeout(warm, 1500);
    }
  }, [checkAuth]);

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    if (typeof window !== "undefined") {
      window.localStorage.removeItem("ybtv_user_token");
    }
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, refresh: checkAuth, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
